import { useClerk, useSignUp, useUser } from "@clerk/tanstack-react-start";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { type FormEvent, useState } from "react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useRedirectAfterAuth } from "@/hooks/use-redirect-after-auth";

type InviteAcceptSearch = {
  __clerk_ticket?: string;
};

export const Route = createFileRoute("/invite/accept")({
  validateSearch: (raw: Record<string, unknown>): InviteAcceptSearch => {
    const t = raw.__clerk_ticket;
    return {
      __clerk_ticket: typeof t === "string" ? t : undefined,
    };
  },
  component: InviteAcceptPage,
});

function InviteAcceptPage() {
  const { isLoaded: userLoaded } = useUser();
  const { isLoaded, signUp } = useSignUp();
  const { setActive } = useClerk();
  const navigate = useNavigate();
  const { __clerk_ticket: ticket } = Route.useSearch();

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [submitError, setSubmitError] = useState<string | null>(null);

  useRedirectAfterAuth();

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setSubmitError(null);
    if (!ticket || !signUp) {
      setSubmitError("Missing invitation. Open the link from your invitation email.");
      return;
    }
    try {
      const attempt = await signUp.create({
        strategy: "ticket",
        ticket,
        firstName,
        lastName,
      });
      if (attempt.status !== "complete" || !attempt.createdSessionId) {
        setSubmitError("Could not complete sign-up. Try again or use the link from your email.");
        return;
      }
      await setActive({ session: attempt.createdSessionId });
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : "Something went wrong.");
    }
  };

  if (!userLoaded || !isLoaded) {
    return (
      <div className="flex flex-1 items-center justify-center px-4">
        <p className="text-muted-foreground">Loading…</p>
      </div>
    );
  }

  if (!ticket) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-4 px-4 py-8">
        <p className="text-muted-foreground text-center">
          This page needs a valid invitation link. Open the link from your invitation email.
        </p>
        <Button variant="outline" onClick={() => void navigate({ to: "/sign-in" })}>
          Sign in
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-1 flex-col items-center justify-center gap-8 px-4 py-8">
      <div className="text-center">
        <h1 className="text-2xl font-bold">Create your account</h1>
        <p className="text-muted-foreground mt-2 text-sm">
          Complete your details to join your organisation.
        </p>
      </div>

      <form className="flex w-full max-w-sm flex-col gap-4" onSubmit={(e) => void handleSubmit(e)}>
        <div className="grid gap-2">
          <Label htmlFor="invite-first-name">First name</Label>
          <Input
            id="invite-first-name"
            name="firstName"
            autoComplete="given-name"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            required
          />
        </div>
        <div className="grid gap-2">
          <Label htmlFor="invite-last-name">Last name</Label>
          <Input
            id="invite-last-name"
            name="lastName"
            autoComplete="family-name"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            required
          />
        </div>

        <div id="clerk-captcha" />

        {submitError ? (
          <p className="text-destructive text-center text-sm" role="alert">
            {submitError}
          </p>
        ) : null}

        <Button type="submit" className="w-full">
          Continue
        </Button>
      </form>
    </div>
  );
}
